import React from 'react';
import './CarRentalResults.css';

/**
 * CarRentalResults Component
 * 
 * Displays car rental options
 * Shows vehicle type, company, price, features
 */
const CarRentalResults = ({ cars, loading, error, onSelectCar }) => {
  
  // Loading state
  if (loading) {
    return (
      <div className="car-results-loading">
        <div className="loading-spinner"></div>
        <p>Searching for rental cars...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="car-results-error">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" strokeLinecap="round" />
          <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <h3>Search Failed</h3>
        <p>{error}</p>
      </div>
    );
  }

  // No results
  if (!cars || cars.length === 0) {
    return (
      <div className="car-results-empty">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
        </svg>
        <h3>No Cars Found</h3>
        <p>Try adjusting your search criteria</p>
      </div>
    );
  }

  return (
    <div className="car-results">
      <div className="results-header">
        <h2>Available Rental Cars</h2>
        <p className="results-count">{cars.length} cars found</p>
      </div>

      <div className="car-list">
        {cars.map((car, index) => (
          <div key={car.offerId || index} className="car-card">
            <div className="car-card-content">
              <div className="car-info">
                <div className="car-header">
                  <div>
                    <h3 className="car-name">
                      {car.carModel || car.carCategory || 'Standard Car'}
                    </h3>
                    <p className="car-company">
                      {car.provider || 'Rental Company'}
                    </p>
                  </div>
                </div>

                <div className="car-features">
                  {car.seats && (
                    <div className="feature">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {car.seats} seats
                    </div>
                  )}

                  {car.automaticTransmission !== undefined && (
                    <div className="feature">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="3" strokeWidth="2" />
                        <path strokeLinecap="round" strokeWidth="2" d="M12 1v6m0 6v6" />
                      </svg>
                      {car.automaticTransmission ? 'Automatic' : 'Manual'}
                    </div>
                  )}

                  {car.airConditioning && (
                    <div className="feature">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                              d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                      </svg>
                      A/C
                    </div>
                  )}

                  {car.freeCancellation && (
                    <div className="feature">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Free Cancellation
                    </div>
                  )}
                </div>
              </div>

              <div className="car-pricing">
                <div className="price-container">
                  <span className="price-amount">${car.totalPrice || 0}</span>
                  <span className="price-period">total</span>
                  {car.pricePerDay && (
                    <span className="price-detail" style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                      ${car.pricePerDay}/day
                    </span>
                  )}
                </div>

                <button
                  className="select-car-button"
                  onClick={() => onSelectCar && onSelectCar(car)}
                >
                  Select Car
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CarRentalResults;